import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, CircleCheck, CircleAlert } from "lucide-react";
import { errorMessage } from "@web/lib/api";
import { calendarApi, calendarConnectionQuery } from "@web/lib/queries-automation";
import { useMe } from "@web/lib/session";
import {
  BottomSheet, Button, Card, Disclosure, Inline, Page, Region, Row, RowGroup, Skeleton, Stack,
  StatusChip, Text, TextField, Workspace, useToast,
} from "@web/ui";
import { AdminsOnly } from "./Settings";

/** Settings › Calendar (SPEC §6.1 item 13, §7.11). Two ways in: let the service
 * account create the calendar, or share an existing one with it and paste the id.
 * Either way staff see events because the calendar is shared with them, not
 * because they are invited to each one. */
export function SettingsCalendar() {
  const me = useMe();
  const toast = useToast();
  const status = useQuery({ ...calendarConnectionQuery, enabled: me.companyAdmin });
  const [connecting, setConnecting] = useState(false);
  const [calendarId, setCalendarId] = useState("");
  const [busy, setBusy] = useState(false);
  const [probe, setProbe] = useState<{ ok: boolean; error?: string } | null>(null);
  if (!me.companyAdmin) return <AdminsOnly />;
  if (status.isError) return <Page><Stack gap={3}><Text tone="danger">The calendar connection could not be read.</Text><Button variant="secondary" onClick={() => void status.refetch()}>Try again</Button></Stack></Page>;
  if (!status.data) return <Page><Skeleton count={4} /></Page>;

  const data = status.data;

  async function run<T>(action: () => Promise<T>, done: string): Promise<T | undefined> {
    setBusy(true);
    try { const result = await action(); toast.success(done); return result; }
    catch (error) { toast.error(errorMessage(error)); return undefined; }
    finally { setBusy(false); }
  }

  return <Page>
    <Workspace variant="form"><Region area="main"><Stack gap={4}>
      <Card><Stack gap={3}>
        <Inline justify="between" align="start" wrap>
          <Stack gap={1}>
            <Text variant="title">Shared booking calendar</Text>
            <Text variant="caption" tone="secondary">Bookings, tours and blocked dates appear on one Google calendar that every active member can see and edit.</Text>
          </Stack>
          <StatusChip tone={!data.configured ? "neutral" : data.calendarId ? "success" : "warn"}>
            {!data.configured ? "Not available yet" : data.calendarId ? "Connected" : "Not connected"}
          </StatusChip>
        </Inline>

        {!data.configured && <Inline gap={2} align="start">
          <CircleAlert aria-hidden size={20} />
          <Text variant="caption" tone="secondary">
            This deployment has no Google service account yet, so nothing is being synced. Everything else in venue365 works normally, and the backlog will sync once it is configured.
          </Text>
        </Inline>}

        {data.configured && data.calendarId && <RowGroup>
          <Row title={data.calendarName ?? "Calendar"} subtitle={data.calendarId} wrap />
          <Row title="Who can see it" subtitle={`Shared with ${data.writers} ${data.writers === 1 ? "person" : "people"}`} wrap />
          {data.pending > 0 && <Row title={`${data.pending} waiting to sync`} subtitle="These go out on the next ten-minute run." wrap />}
          {data.stuck > 0 && <Row title={`${data.stuck} could not be synced`} subtitle={data.lastError ? `Last problem: ${data.lastError}` : "These have failed repeatedly and need a person."} wrap />}
        </RowGroup>}

        {data.configured && <Inline gap={2} wrap>
          {!data.calendarId && <Button fullWidth={false} loading={busy} onClick={() => void run(calendarApi.setup, "Calendar created and shared")}>Create the calendar</Button>}
          {data.calendarId && <Button variant="secondary" fullWidth={false} loading={busy} onClick={() => void run(calendarApi.share, "Sharing brought up to date")}>Update who can see it</Button>}
          {data.calendarId && <Button variant="secondary" fullWidth={false} loading={busy} onClick={async () => {
            const result = await run(calendarApi.test, "Connection checked");
            if (result) setProbe(result);
          }}>Test the connection</Button>}
          <Button variant="secondary" fullWidth={false} onClick={() => { setCalendarId(data.calendarId ?? ""); setConnecting(true); }}>
            {data.calendarId ? "Use a different calendar" : "Use an existing calendar"}
          </Button>
        </Inline>}

        {probe && <Inline gap={2} align="start">
          {probe.ok ? <CircleCheck aria-hidden size={20} /> : <CircleAlert aria-hidden size={20} />}
          <Text variant="caption" tone={probe.ok ? "secondary" : "danger"}>
            {probe.ok ? "A test event was created and removed. The connection works." : `The test failed: ${probe.error ?? "unknown reason"}`}
          </Text>
        </Inline>}
      </Stack></Card>

      <Card><Stack gap={2}>
        <Text variant="title">Using a calendar you already have</Text>
        <Text variant="caption" tone="secondary">
          In Google Calendar, share the calendar with the address below as "Make changes to events", then paste its calendar id here.
        </Text>
        {data.serviceAccountEmail
          ? <Row title="Share it with" subtitle={data.serviceAccountEmail} wrap />
          : <Text variant="caption" tone="secondary">The address appears here once the service account is configured.</Text>}
        <Disclosure title="Where to find the calendar id">
          <Text variant="caption" tone="secondary">
            In Google Calendar, open Settings, choose the calendar in the left column, and scroll to "Integrate calendar". The calendar id looks like an email address.
          </Text>
        </Disclosure>
      </Stack></Card>

      <Card><Stack gap={2}>
        <Text variant="title">What appears on it</Text>
        <RowGroup>
          <Row icon={<CalendarDays aria-hidden size={22} />} title="Bookings" subtitle="From the agreement going out until the event is finished, plus confirmed holds" wrap />
          <Row icon={<CalendarDays aria-hidden size={22} />} title="Tours" subtitle="While they are scheduled" wrap />
          <Row icon={<CalendarDays aria-hidden size={22} />} title="Blocked dates" subtitle="So nobody books around a gap that is not there" wrap />
        </RowGroup>
        <Text variant="caption" tone="tertiary">Nobody is invited to these events. Staff see them because the calendar is shared with them.</Text>
      </Stack></Card>
    </Stack></Region></Workspace>

    <BottomSheet open={connecting} onClose={() => setConnecting(false)} title="Use an existing calendar"
      footer={<Button loading={busy} disabled={!calendarId.trim()} onClick={async () => {
        await run(() => calendarApi.connect(calendarId.trim()), "Calendar connected");
        setConnecting(false);
      }}>Connect it</Button>}>
      <Stack gap={3}>
        <Text tone="secondary">Share the calendar with {data.serviceAccountEmail ?? "the service account"} first, giving it permission to make changes.</Text>
        <TextField label="Calendar id" value={calendarId} placeholder="abc123@group.calendar.google.com" onChange={event => setCalendarId(event.target.value)} />
        {data.calendarId && <Button variant="secondary" disabled={busy} onClick={async () => {
          await run(() => calendarApi.connect(null), "Calendar disconnected");
          setConnecting(false);
        }}>Disconnect the current calendar</Button>}
      </Stack>
    </BottomSheet>
  </Page>;
}
